/**
 * PrepNoteRow — Single row inside SessionPrepCard.
 *
 * Icon badge colour-coded by note type:
 *   improving  → green badge, trending-down icon
 *   persistent → red badge, alert-circle icon
 *   suggested  → purple badge, bulb icon
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import type { TFunction } from '@dramadetector/shared';

type NoteType = 'improving' | 'persistent' | 'suggested';

interface PrepNoteRowProps {
  type: NoteType;
  text: string;
  t: TFunction<'practitioner'>;
}

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

const NOTE_TYPE_CONFIG: Record<
  NoteType,
  { badgeBg: string; iconName: IoniconName; iconColor: string; labelKey: string }
> = {
  improving: {
    badgeBg: '#DCFCE7',
    iconName: 'trending-down',
    iconColor: '#059669',
    labelKey: 'prep_note.improving',
  },
  persistent: {
    badgeBg: '#FEE2E2',
    iconName: 'alert-circle',
    iconColor: '#DC2626',
    labelKey: 'prep_note.persistent',
  },
  suggested: {
    badgeBg: '#EDE9FE',
    iconName: 'bulb-outline',
    iconColor: '#7C3AED',
    labelKey: 'prep_note.suggested',
  },
};

export const PrepNoteRow: React.FC<PrepNoteRowProps> = React.memo(function PrepNoteRow({
  type,
  text,
  t,
}) {
  const config = NOTE_TYPE_CONFIG[type];

  return (
    <View style={styles.row}>
      <View style={[styles.iconBadge, { backgroundColor: config.badgeBg }]}>
        <Ionicons name={config.iconName} size={12} color={config.iconColor} />
      </View>
      <Text style={styles.body} numberOfLines={0}>
        <Text style={styles.typeLabel}>
          {t(config.labelKey as never)}:{' '}
        </Text>
        {text}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  } as ViewStyle,
  iconBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
  },
  body: {
    fontSize: 13,
    color: '#374151',
    flex: 1,
    lineHeight: 18,
  },
  typeLabel: {
    fontWeight: '600',
  },
});
